import { html, LitElement, TemplateResult } from "lit";
import { customElement } from "lit/decorators.js";

import { SlhCoreFormElement } from "@slhuicore/base/form";
import { SlhFormIcons } from "@slhui/form-icons";
import { SlhCoreTextfield, SlhCoreTextfieldIcon } from "@slhuicore/textfield";
import { Translatable } from "@slhui/i18n/src/mixins/translatable";
import * as Constants from "./textfield.constants";
import { style } from "./assets/textfield.generated-style";

/**
 * Allow users to add input in a single line.
 * @CustomElement slh-textfield
 *
 * @cssprop --slh-default-font-family - Font family.
 * @cssprop --slh-base-xs-font-size - Font size.
 * @cssprop --slh-base-semi-bold-font-weight - Font weight.
 * @cssprop --slh-base-xxs-line-height - Line height.
 * @cssprop --slh-color-typo-100 - Font color, hover placeholder color.
 * @cssprop --slh-color-neutral-shade-00 - Textfield background color.
 * @cssprop --slh-color-neutral-shade-10 - Disabled textfield background color.
 * @cssprop --slh-color-neutral-shade-20 - Border color. Disabled textfield icon color (fill).
 * @cssprop --slh-color-neutral-shade-40 - Input placeholder text color. Icon color (fill). Clear icon color (fill). Textfield hover border color. Disabled textfield font color.
 * @cssprop --slh-color-neutral-shade-80 - Hover font color.
 * @cssprop --slh-color-primary-shade-20 - Active background color.
 * @cssprop --slh-color-primary-shade-60 - Active hover border color. Hint font color.
 * @cssprop --slh-color-error - Error state label color.
 * @cssprop --slh-color-error-shade-20 - Error state background color.
 * @cssprop --slh-color-error-shade-60 - Error state border color.
 * @cssprop --slh-color-error-shade-80 - Error state hober border color.
 * @cssprop --slh-color-error-shade-40 - Drop shadow color for focused error state textfield.
 * @cssprop --slh-color-primary-shade-60 - Focused drop shadow.
 * @cssprop --slh--base-bold-font-weight - Textfield hint font weight.
 * @cssprop --slh--base-xxs-font-size - Textfield hint font size.
 *
 * ** slots
 * @slot default - Optional label above the input field
 * @slot icon-prepend - Custom content on the left side of the input field.
 * @slot icon-append - Custom content on the right side of the input field. Used also for clearable functionality.
 * @slot hint - Custom content below the textfield, designed to contain recommendations for user input.
 */
@customElement("slh-textfield")
export default class SlhTextfield
  extends Translatable(SlhCoreTextfield(LitElement))
  implements SlhCoreFormElement
{
  static styles = style;

  /**
   * Render icon template
   * @returns {TemplateResult}
   * @param {SlhCoreTextfieldIcon} icon
   */
  // eslint-disable-next-line class-methods-use-this
  private renderIcon(icon: SlhCoreTextfieldIcon): TemplateResult {
    return html`<slh-icon
      type="${icon.type}"
      color="${icon?.color}"
      focusable="${icon?.focusable}"
      aria-label="${icon?.ariaLabel}"
    ></slh-icon>`;
  }

  // eslint-disable-next-line class-methods-use-this
  private getClearIcon(): string {
    return Constants.CLEARABLE_ICON_TYPE;
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.isClearable()) {
      this.addEventListener("click", this.handleClick.bind(this));
    }
  }

  /**
   * Remove "focus-visible" css class of textfield container after clearable icon mouse click
   */
  handleClick(event: any) {
    if (event.target.tagName === "SLH-ICON" && event.target.focusable) {
      if (event instanceof PointerEvent) {
        this.containerElement?.classList.remove("focus-visible");
      }
    }
  }

  disconnectedCallback() {
    if (this.isClearable()) {
      this.removeEventListener("click", this.handleClick.bind(this));
    }
    super.disconnectedCallback();
  }

  // eslint-disable-next-line class-methods-use-this
  private getHintIcon(): HTMLElement {
    return SlhFormIcons.getHintIcon();
  }
}
